import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { Prescription } from '../../models/prescription';
import { DatePipe } from '@angular/common';
import {
  TuiChip,
  TuiPdfViewerOptions,
  TuiPdfViewerService,
} from '@taiga-ui/kit';
import { TuiButton, tuiDialog } from '@taiga-ui/core';
import {
  PrescriptionForm,
  PrescriptionFormList,
} from '../../models/prescriptionForm/prescriptionForm';
import { User } from '../../models/user';
import { PrescriptionFormPpcComponent } from '../../prescription-form/prescription-form-ppc/prescription-form-ppc.component';
import { PrescriptionFormAvfComponent } from '../../prescription-form/prescription-form-avf/prescription-form-avf.component';
import { PrescriptionFormAerosolsComponent } from '../../prescription-form/prescription-form-aerosols/prescription-form-aerosols.component';
import { PrescriptionFormService } from '../../services/prescription-form.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PrescriptionFormAutreComponent } from '../../prescription-form/prescription-form-autre/prescription-form-autre.component';
import { Organisme } from '../../models/organisme';
import { Patient } from '../../models/patient';
import { PrescriptionFormDialogData } from '../../models/prescription-form-dialog-data';
import { CodeActiviteService } from '../../services/code-activite.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-prescription-row',
  standalone: true,
  imports: [TuiButton, TuiChip, DatePipe],
  templateUrl: './prescription-row.component.html',
  styleUrl: './prescription-row.component.scss',
})
export class PrescriptionRowComponent {
  @Output() formCompleted = new EventEmitter<void>();

  private readonly pdfService = inject(TuiPdfViewerService);
  private readonly sanitizer = inject(DomSanitizer);

  private prescriptionFormService = inject(PrescriptionFormService);
  protected authService = inject(AuthService);
  protected codeActiviteService = inject(CodeActiviteService);

  private readonly formPpc = tuiDialog(PrescriptionFormPpcComponent);
  private readonly formAvf = tuiDialog(PrescriptionFormAvfComponent);
  private readonly formAerosols = tuiDialog(PrescriptionFormAerosolsComponent);
  private readonly formAutre = tuiDialog(PrescriptionFormAutreComponent, {
    size: 'l',
  });

  @Input({ required: true }) prescription!: Prescription;
  @Input({ required: true }) userList?: User[];
  @Input({ required: true }) prescriptionFormList?: PrescriptionFormList;
  @Input({ required: true }) organisme?: Organisme | null;
  @Input({ required: true }) patient!: Patient;
  @Input({ required: true }) activite!: string;

  openPdf(
    actions: PolymorpheusContent<TuiPdfViewerOptions>,
    prescriptionForm: PrescriptionForm,
  ): void {
    try {
      if (!prescriptionForm.id) {
        return;
      }

      this.prescriptionFormService.pdf(prescriptionForm.id).subscribe({
        next: (blob) => {
          const url: SafeResourceUrl =
            this.sanitizer.bypassSecurityTrustResourceUrl(
              URL.createObjectURL(blob),
            );

          this.pdfService
            .open(url, {
              label: 'Prescription',
              actions,
            })
            .subscribe();
        },
      });
    } catch (error) {
      console.error("Erreur lors de l'ouverture de la prescription : ", error);
    }
  }

  dernierePrescriptionForm(
    codeDActivite: 'PPC' | 'O2' | 'VENTILATION' | 'AVF' | 'AEROSOLS' | 'O2VNI',
  ): PrescriptionForm | undefined {
    const forms = this.prescriptionFormList?.list || [];

    const activityForms = forms.filter(
      (form) => form.codeDActivite === codeDActivite,
    );

    // Retourne la prescription la plus ancienne de l'activité
    return activityForms.length > 0
      ? activityForms.reduce((prev, current) =>
          current.dateDePrescription < prev.dateDePrescription ? prev : current,
        )
      : undefined;
  }

  async openForm(
    prescription: Prescription,
    codeDActivite: 'PPC' | 'O2' | 'VENTILATION' | 'AVF' | 'AEROSOLS' | 'O2VNI',
    reedition: boolean = false,
  ) {
    if (this.patient && this.userList && this.organisme !== undefined) {
      switch (codeDActivite) {
        case 'PPC':
          var form = this.formPpc;
          break;
        case 'O2':
          var form = this.formAutre;
          break;
        case 'VENTILATION':
          var form = this.formAutre;
          break;
        case 'AVF':
          var form = this.formAvf;
          break;
        case 'AEROSOLS':
          var form = this.formAerosols;
          break;
        case 'O2VNI':
          var form = this.formAutre;
          break;
        default:
          return;
          break;
      }

      var prescriptionActuelle = this.dernierePrescriptionForm(codeDActivite);

      form(
        new PrescriptionFormDialogData(
          this.patient,
          prescription,
          codeDActivite,
          prescriptionActuelle,
          this.userList,
          this.organisme,
          reedition,
        ),
      ).subscribe({
        complete: () => {
          this.formCompleted.emit();
        },
      });
    }
  }

  onDelete(prescriptionForm: PrescriptionForm): void {
    this.prescriptionFormService.delete(prescriptionForm).subscribe({
      next: (data) => {
        this.formCompleted.emit();
      },
      error: (error) => {
        console.log(error);
      },
    });
  }
}
